import { abs, clamp, dot, exp, float, max, vec3, vec4 } from 'three/tsl';
import type { Vector3 } from 'three/webgpu';
import { mulberry32 } from '../math/rng';
import { gnoised as gnoisedCpu } from './terrainHeight';
import { gnoised as gnoisedGpu, NOISE_PERIOD } from './terrainNoise';

type N = any;

/**
 * How a world's ground is shared out between its biomes (up to four). Every biome scores each
 * place: its own low-frequency noise, a constant that sets its share of the ground, and its
 * leanings toward high or low ground and toward the poles or the equator. The scores go
 * through a softmax (the `sharpness` sets how wide the borders are), and weights too small to
 * matter are dropped, so most ground is exactly one biome and only the borders blend.
 *
 * The same numbers come out on the CPU (for planning: the editor's map, where caves open,
 * which vegetation layers are near) and on the GPU (TileGenerator writes them into its biome
 * atlas, where the terrain and the plants read them).
 */
export interface BiomeFieldDef {
  /** Size of the regions (m): the wavelength of the biome noise. */
  wavelength: number;
  /** Narrowness of the borders (the softmax's inverse temperature, ~4–20). */
  sharpness: number;
  biomes: {
    /** Added to the biome's score everywhere: its share of the ground (see calibrateBiomes). */
    offset: number;
    /** Leans toward high ground (+) or low ground (−), about −2 … 2. */
    height: number;
    /** Leans toward the poles (+) or the equator (−), about −2 … 2. */
    polar: number;
  }[];
  /** The world's poles (hollow frame, unit). */
  axis: [number, number, number];
  /** Height (m) that counts as high ground, as much as sea level counts as low. */
  heightRef: number;
  /** The world's radius (m) and a seed for the noise. */
  radius: number;
  seed: number;
}

/** After the softmax, weights below this are dropped (and the rest renormalised). */
const CUT = 0.03;
/** Biome noise octaves: each this many times finer than the last. */
const LACUNARITY = 2.07;
/**
 * Their strengths. Two octaves drew smooth blobs with crisp outlines, like paint on a map; the
 * two finer ones make the borders ragged, as ecotones are. (Their lattice offsets are drawn after
 * every biome's first two, so a world's regions keep the shapes they had.)
 */
const AMPS = [1, 0.5, 0.22, 0.1];
/** The steepest a biome's noise (all its octaves) climbs, per wavelength (4.2 measured; see its test). */
export const NOISE_SLOPE = 5.2;
export const MAX_BIOMES = 4;

export class BiomeField {
  readonly count: number;
  /** Per biome, per octave: the noise's lattice offset (in [0, PERIOD)). */
  private readonly offsets: [number, number, number][][];
  private readonly scratch: number[] = [0, 0, 0, 0];

  constructor(readonly def: BiomeFieldDef) {
    this.count = Math.min(def.biomes.length, MAX_BIOMES);
    const rnd = mulberry32(def.seed ^ 0x62696f6d);
    const offset = () =>
      [rnd() * NOISE_PERIOD, rnd() * NOISE_PERIOD, rnd() * NOISE_PERIOD] as [number, number, number];
    this.offsets = def.biomes.map(() => [offset(), offset()]);
    for (const o of this.offsets) for (let k = 2; k < AMPS.length; k++) o.push(offset());
  }

  /**
   * Each biome's score without its offset, at unit direction `dir` (hollow frame) where the
   * ground is `h` metres high: for calibrating the offsets, which don't change the rest.
   */
  rawScores(dir: Vector3, h: number, out: number[] = []): number[] {
    const d = this.def;
    const f = d.radius / d.wavelength;
    const hn = heightTerm(h, d.heightRef);
    const lat = Math.abs(dir.x * d.axis[0] + dir.y * d.axis[1] + dir.z * d.axis[2]);
    for (let i = 0; i < this.count; i++) {
      let n = 0;
      let k = f;
      for (let j = 0; j < AMPS.length; j++) {
        const o = this.offsets[i][j];
        n += AMPS[j] * gnoisedCpu(wrap(dir.x * k + o[0]), wrap(dir.y * k + o[1]), wrap(dir.z * k + o[2]))[0];
        k *= LACUNARITY;
      }
      const b = d.biomes[i];
      out[i] = n + b.height * hn + b.polar * (2 * lat - 1);
    }
    out.length = this.count;
    return out;
  }

  /** Each biome's weight at `dir` (unit, hollow frame) over ground `h` metres high; they sum to 1. */
  weights(dir: Vector3, h: number, out: number[] = []): number[] {
    const s = this.rawScores(dir, h, this.scratch);
    for (let i = 0; i < this.count; i++) s[i] += this.def.biomes[i].offset;
    return softmaxCut(s, this.count, this.def.sharpness, out);
  }

  /**
   * The biomes that may reach weight `least` somewhere on a patch of ground round unit direction
   * `dir` (hollow frame), `halfWidth` metres across from its centre to its corners, whose ground
   * lies `hLo` … `hHi` metres high: bit i for biome i. It may name a biome that doesn't get
   * there, never leave out one that does: each score may drift across the patch by as much as
   * its noise and latitude can, and each rival's height lean is taken at its least.
   */
  reachIn(dir: Vector3, halfWidth: number, hLo: number, hHi: number, least: number): number {
    const d = this.def;
    // (Scores over ground at 0 m: the height leans are put back below, at their extremes.)
    const s = this.rawScores(dir, 0, this.scratch);
    const h0 = heightTerm(0, d.heightRef);
    const lo = heightTerm(hLo, d.heightRef) - h0;
    const hi = heightTerm(hHi, d.heightRef) - h0;
    const drift = (NOISE_SLOPE * halfWidth) / d.wavelength;
    const dLat = (2 * halfWidth) / d.radius;
    let bits = 0;
    for (let k = 0; k < this.count; k++) {
      const bk = d.biomes[k];
      // The most weight k can have: every rival's score as far below k's as the patch allows.
      let rivals = 0;
      for (let j = 0; j < this.count; j++) {
        if (j === k) continue;
        const bj = d.biomes[j];
        const lean = bj.height - bk.height;
        const gap =
          s[j] +
          bj.offset -
          (s[k] + bk.offset) +
          Math.min(lean * lo, lean * hi) -
          2 * drift -
          Math.abs(bj.polar - bk.polar) * dLat;
        rivals += Math.exp(gap * d.sharpness);
      }
      if (1 / (1 + rivals) >= least) bits |= 1 << k;
    }
    return bits;
  }

  /**
   * The weights as a vec4 node (unused biomes 0), at the unit direction `dir` (hollow frame)
   * over ground `h` metres high.
   */
  node(dir: N, h: N): N {
    const d = this.def;
    const f = d.radius / d.wavelength;
    const hn = clamp(h.div(d.heightRef).sub(0.5), -1, 1.5);
    const lat = abs(dot(dir, vec3(...d.axis)));
    const scores: N[] = [];
    for (let i = 0; i < this.count; i++) {
      let n: N = float(0);
      let k = f;
      for (let j = 0; j < AMPS.length; j++) {
        n = n.add(gnoisedGpu(dir.mul(k).add(vec3(...this.offsets[i][j]))).x.mul(AMPS[j]));
        k *= LACUNARITY;
      }
      const b = d.biomes[i];
      scores.push(n.add(hn.mul(b.height)).add(lat.mul(2).sub(1).mul(b.polar)).add(b.offset));
    }
    let top: N = scores[0];
    for (let i = 1; i < scores.length; i++) top = max(top, scores[i]);
    const e = scores.map((s) => exp(s.sub(top).mul(d.sharpness)));
    let sum: N = e[0];
    for (let i = 1; i < e.length; i++) sum = sum.add(e[i]);
    // Drop the faint ones (so most ground runs one palette, not four), then renormalise.
    const kept = e.map((x) => max(x.div(sum).sub(CUT), 0));
    let total: N = kept[0];
    for (let i = 1; i < kept.length; i++) total = total.add(kept[i]);
    const w = kept.map((x) => x.div(max(total, 1e-6)));
    while (w.length < 4) w.push(float(0));
    return vec4(w[0], w[1], w[2], w[3]);
  }
}

/** The softmax of scores `s` (first `n`), faint weights dropped, renormalised. */
export function softmaxCut(s: number[], n: number, sharpness: number, out: number[] = []): number[] {
  let top = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < n; i++) top = Math.max(top, s[i]);
  let sum = 0;
  for (let i = 0; i < n; i++) {
    out[i] = Math.exp((s[i] - top) * sharpness);
    sum += out[i];
  }
  let total = 0;
  for (let i = 0; i < n; i++) {
    out[i] = Math.max(out[i] / sum - CUT, 0);
    total += out[i];
  }
  for (let i = 0; i < n; i++) out[i] /= Math.max(total, 1e-6);
  out.length = n;
  return out;
}

/**
 * Offsets that give each biome about its `shares` of the ground (they need not sum to 1),
 * found from sample places (directions and their heights): a few dozen rounds of nudging each
 * offset by how far its mean weight is from its share. Deterministic.
 */
export function calibrateBiomes(
  field: BiomeField,
  samples: { dir: Vector3; h: number }[],
  shares: number[],
): number[] {
  const n = field.count;
  const total = shares.slice(0, n).reduce((a, b) => a + Math.max(b, 1e-3), 0);
  const target = shares.slice(0, n).map((s) => Math.max(s, 1e-3) / total);
  const raw = samples.map((s) => field.rawScores(s.dir, s.h, []));
  const offsets = new Array(n).fill(0);
  const s: number[] = [];
  const w: number[] = [];
  for (let round = 0; round < 60; round++) {
    const mean = new Array(n).fill(0);
    for (const r of raw) {
      for (let i = 0; i < n; i++) s[i] = r[i] + offsets[i];
      softmaxCut(s, n, field.def.sharpness, w);
      for (let i = 0; i < n; i++) mean[i] += w[i] / raw.length;
    }
    for (let i = 0; i < n; i++) offsets[i] += 0.35 * Math.log((target[i] + 0.005) / (mean[i] + 0.005));
    // Only differences matter: keep them centred.
    const avg = offsets.reduce((a, b) => a + b, 0) / n;
    for (let i = 0; i < n; i++) offsets[i] -= avg;
  }
  return offsets;
}

function heightTerm(h: number, ref: number): number {
  return Math.min(Math.max(h / ref - 0.5, -1), 1.5);
}

function wrap(x: number): number {
  const r = x % NOISE_PERIOD;
  return r < 0 ? r + NOISE_PERIOD : r;
}
